import { createContext, useContext } from 'react';
import type { SocketExpressionBuilderController } from './useSocketExpressionBuilder';

/**
 * How canvas nodes talk to the socket expression builder open inline on the
 * editor canvas. Split in two so the stable half (actions) never re-renders a
 * node when only the picking state changes.
 *
 * Both default to `null`: a canvas without a builder (the read-only graph
 * view) simply renders no builder affordances.
 */
export interface SocketExpressionBuilderActions {
  /** Open the builder on an input socket. */
  open: (nodeId: string, socketId: string) => void;
  /** Fill the waiting slot with a canvas output socket. */
  pickSource: (stepId: string, socketId: string) => void;
  /** Whether this user may change expressions; otherwise the builder is a view. */
  canEdit: boolean;
}

export interface SocketExpressionBuilderView {
  /** The socket the builder is open on. */
  editing: { nodeId: string; socketId: string } | null;
  /**
   * Set while a slot waits for an output socket: the keys
   * (`stepId:socketId`) a click may pick, the rest dims.
   */
  pickableSources: ReadonlySet<string> | null;
  /** Sources the open expression reads, highlighted on the canvas. */
  usedSources: ReadonlySet<string>;
}

export const SocketExpressionBuilderActionsContext =
  createContext<SocketExpressionBuilderActions | null>(null);

export const SocketExpressionBuilderViewContext =
  createContext<SocketExpressionBuilderView | null>(null);

export function useSocketExpressionBuilderActions() {
  return useContext(SocketExpressionBuilderActionsContext);
}

export function useSocketExpressionBuilderView() {
  return useContext(SocketExpressionBuilderViewContext);
}

/**
 * The open builder itself, for the inline builder node the overlay draws on
 * the canvas. Only the editor provides it.
 */
export const SocketExpressionBuilderControllerContext =
  createContext<SocketExpressionBuilderController | null>(null);

export function useSocketExpressionBuilderController() {
  return useContext(SocketExpressionBuilderControllerContext);
}
